import { Emoji } from '@emoji-mart/data';
import { RecurrenceOptions } from 'src/@types/recurrence';
import { Roles } from 'src/features/workspace/WorkspaceShareMemberRoleSelectProps';
import { IConversationMessage } from './conversation';
import { Note } from './notes-transcription';
import { IWorkspaceUser, WorkspaceParticipant } from './workspace-info';

export interface IEmoji extends Emoji {
  shortcodes: string;
}

export type IChatAttachment = {
  name: string;
  size: number;
  type: string;
  path: string;
  preview: string;
  dateCreated: Date;
  dateModified: Date;
};

export type IChatContact = IWorkspaceUser;

export enum ChatChannelType {
  TEXT = 'text',
  VOICE = 'voice',
  MEET = 'meet',
}

export enum ChatMuteUntil {
  HOURS_8 = 'hours_8',
  DAY = 'day',
  WEEK = 'week',
}

export enum ChatMuteType {
  NOTMUTED = 'not-muted',
  MENTIONS = 'mentions',
  MUTED = 'muted',
  MUTEDUNTIL = 'muted-until',
}

export enum ChatMessageSettingsType {
  ALL = 'all',
  ADMINS = 'admins',
}

export interface ChatSettingsType
  extends ChatTextSettingsType,
    ChatMeetSettingsType,
    ChatVoiceSettingsType {}

export type ChatTextSettingsType = {
  canSend?: ChatMessageSettingsType;
  canReply?: ChatMessageSettingsType;
  canView?: ChatMessageSettingsType;
};

export type ChatTextMuteSettingsType = {
  mute: ChatMuteType;
  muteUntil?: string;
};

export type ChatMeetSettingsType = {
  muteMic?: boolean;
  offVideo?: boolean;
  muteParticipants?: boolean;
  offParticipantsVideo?: boolean;
};

export type ChatVoiceSettingsType = {
  language?: ChatChannelLanguageSettings;
  enableAutoTranscriptionWhenCreated?: boolean;
  allParticipantsAccessTranscription?: boolean;
  administratorsAccessTranscription?: boolean;
  specificUsersAccessTranscription?: boolean;
  transcriptionUsers?: WorkspaceParticipant[] | string[];
  externalUsersReceiveEmailSummary?: boolean;
  participantsReceiveEmailSummary?: boolean;
  specificUsersReceiveEmailSummary?: boolean;
  emailSummaryUsers?: WorkspaceParticipant[] | string[];
  useCustomSummary?: boolean;
  customSummaryPrompt?: string;
};

export type IChatChannelCallSchedulePayload = Omit<
  IChatChannelCallSchedule,
  'participants'
> & {
  participants: string[];
};

export type IChatChannelCallSchedule = {
  _id?: string;
  title: string;
  description?: string;
  externalLink?: string;
  participants?: WorkspaceParticipant[];
  externalParticipantsEmails?: string[];
  callSchedule: RecurrenceOptions;
  channel?: string;
  callScheduleData?: {
    nextExecution: string;
    rule: string;
  };
};

export type LastReadMessageSocketType = {
  channelId: string;
  messages: string[];
  workspaceId: string;
  parentId?: string;
};

export type AICallChannel = IChatConversation & {
  aiCall: boolean;
};

export type IChatConversation = {
  _id: string;
  name: string;
  type: ChatChannelType;
  order: number;
  picture?: string;
  icon?: string;
  p2p?: boolean;
  workspace: string;
  participants: WorkspaceParticipant[];
  messages: IChatMessage[];
  hasUnreadMessages: boolean;
  unreadMessages?: string[]; // TODO Deixei opcional por que tava dando erro no <ChatVoiceChannel conversation={} /> @dashboard/meet/JoinConferencePage.tsx:182:29
  lastReadMessage?: string;
  description: string;
  createdAt: string;
  createdBy?: string;
  settings?: ChatSettingsType;
  muteSettings?: ChatTextMuteSettingsType;
  subchannels: IChatConversation[];
  parentId?: string;
  parentMessageId?: string;
  followers?: string[];
  roles: Record<Roles, Record<string, true>>;
  hidden?: Record<string, boolean> | true; //? | true here pq aparentemente o backend retorna true quando eh p2p e hidden
  egressId?: string;
  threadNumber?: number;
  callSchedules?: IChatChannelCallSchedule[];
  chatPartner?: { user: string };
  externalLink?: string;
};

export type ChatMessageListItem = IChatMessage & {
  isSameAuthorAsPreviousMessage: boolean;
  isSameDayAsPreviousMessage: boolean;
  messages?: string[];
};

export type IChatMessage = IConversationMessage & {
  channel: string;
  attachments: IChatAttachment[];
  forwarded?: boolean;
  pinned?: boolean;
  threadMessagesCount?: number;
  threadLastMessage?: ChatMessageListItem;
};

export type IChatSendMessage = Pick<
  IChatMessage,
  | 'channel'
  | 'content'
  | 'contentType'
  | 'mediaRef'
  | 'mimeType'
  | 'fileName'
  | 'fileSize'
  | 'forwarded'
  | 'pinned'
  | 'soundBoard'
> & { replyTo?: string };

export type IChatContactsState = {
  byId: Record<string, IWorkspaceUser>;
  allIds: string[];
};

export type IChatConversationsState = {
  byId: Record<string, IChatConversation>;
  allIds: string[];
};

export type IChatState = {
  isLoading: boolean;
  error: Error | string | null;
  contacts: IChatContactsState;
  conversations: IChatConversationsState;
  activeConversationId: null | string;
  participants: WorkspaceParticipant[];
  recipients: IWorkspaceUser[];
};

export type IChatUserPermissions = {
  sendMessage: boolean;
  manageMembers: boolean;
  canEditChannel: boolean;
  canDeleteChannel: boolean;
  canEditSettings: boolean;
};

export type IChatSession = {
  _id: string;
  active?: boolean;
  chatChannel: string;
  startAt: string;
  endAt: string;
  participants: WorkspaceParticipant[];
  egressSessions: string[];
  hasChat?: boolean;
  transcriptions?: Note[];
};

export enum ChatChannelLanguageSettings {
  en = 'en',
  en_au = 'en_au',
  en_uk = 'en_uk',
  en_us = 'en_us',
  es = 'es',
  fr = 'fr',
  de = 'de',
  it = 'it',
  pt = 'pt',
  nl = 'nl',
  af = 'af',
  sq = 'sq',
  am = 'am',
  ar = 'ar',
  hy = 'hy',
  as = 'as',
  az = 'az',
  ba = 'ba',
  eu = 'eu',
  be = 'be',
  bn = 'bn',
  bs = 'bs',
  br = 'br',
  bg = 'bg',
  my = 'my',
  ca = 'ca',
  zh = 'zh',
  hr = 'hr',
  cs = 'cs',
  da = 'da',
  et = 'et',
  fo = 'fo',
  fi = 'fi',
  gl = 'gl',
  ka = 'ka',
  el = 'el',
  gu = 'gu',
  ht = 'ht',
  ha = 'ha',
  haw = 'haw',
  he = 'he',
  hi = 'hi',
  hu = 'hu',
  is = 'is',
  id = 'id',
  ja = 'ja',
  jw = 'jw',
  kn = 'kn',
  kk = 'kk',
  km = 'km',
  ko = 'ko',
  lo = 'lo',
  la = 'la',
  lv = 'lv',
  ln = 'ln',
  lt = 'lt',
  lb = 'lb',
  mk = 'mk',
  mg = 'mg',
  ms = 'ms',
  ml = 'ml',
  mt = 'mt',
  mi = 'mi',
  mr = 'mr',
  mn = 'mn',
  ne = 'ne',
  no = 'no',
  nn = 'nn',
  oc = 'oc',
  pa = 'pa',
  ps = 'ps',
  fa = 'fa',
  pl = 'pl',
  ro = 'ro',
  ru = 'ru',
  sa = 'sa',
  sr = 'sr',
  sn = 'sn',
  sd = 'sd',
  si = 'si',
  sk = 'sk',
  sl = 'sl',
  so = 'so',
  su = 'su',
  sw = 'sw',
  sv = 'sv',
  tl = 'tl',
  tg = 'tg',
  ta = 'ta',
  tt = 'tt',
  te = 'te',
  th = 'th',
  bo = 'bo',
  tr = 'tr',
  tk = 'tk',
  uk = 'uk',
  ur = 'ur',
  uz = 'uz',
  vi = 'vi',
  cy = 'cy',
  yi = 'yi',
  yo = 'yo',
}

export type LanguagesType = {
  code: ChatChannelLanguageSettings;
  name: string;
};

export const languagesObject: LanguagesType[] = [
  { code: ChatChannelLanguageSettings.en, name: 'Global English' },
  { code: ChatChannelLanguageSettings.en_au, name: 'Australian English' },
  { code: ChatChannelLanguageSettings.en_uk, name: 'British English' },
  { code: ChatChannelLanguageSettings.en_us, name: 'American English' },
  { code: ChatChannelLanguageSettings.es, name: 'Spanish' },
  { code: ChatChannelLanguageSettings.fr, name: 'French' },
  { code: ChatChannelLanguageSettings.de, name: 'German' },
  { code: ChatChannelLanguageSettings.it, name: 'Italian' },
  { code: ChatChannelLanguageSettings.pt, name: 'Portuguese' },
  { code: ChatChannelLanguageSettings.nl, name: 'Dutch' },
  { code: ChatChannelLanguageSettings.af, name: 'Afrikaans' },
  { code: ChatChannelLanguageSettings.sq, name: 'Albanian' },
  { code: ChatChannelLanguageSettings.am, name: 'Amharic' },
  { code: ChatChannelLanguageSettings.ar, name: 'Arabic' },
  { code: ChatChannelLanguageSettings.hy, name: 'Armenian' },
  { code: ChatChannelLanguageSettings.as, name: 'Assamese' },
  { code: ChatChannelLanguageSettings.az, name: 'Azerbaijani' },
  { code: ChatChannelLanguageSettings.ba, name: 'Bashkir' },
  { code: ChatChannelLanguageSettings.eu, name: 'Basque' },
  { code: ChatChannelLanguageSettings.be, name: 'Belarusian' },
  { code: ChatChannelLanguageSettings.bn, name: 'Bengali' },
  { code: ChatChannelLanguageSettings.bs, name: 'Bosnian' },
  { code: ChatChannelLanguageSettings.br, name: 'Breton' },
  { code: ChatChannelLanguageSettings.bg, name: 'Bulgarian' },
  { code: ChatChannelLanguageSettings.my, name: 'Burmese' },
  { code: ChatChannelLanguageSettings.ca, name: 'Catalan' },
  { code: ChatChannelLanguageSettings.zh, name: 'Chinese' },
  { code: ChatChannelLanguageSettings.hr, name: 'Croatian' },
  { code: ChatChannelLanguageSettings.cs, name: 'Czech' },
  { code: ChatChannelLanguageSettings.da, name: 'Danish' },
  { code: ChatChannelLanguageSettings.et, name: 'Estonian' },
  { code: ChatChannelLanguageSettings.fo, name: 'Faroese' },
  { code: ChatChannelLanguageSettings.fi, name: 'Finnish' },
  { code: ChatChannelLanguageSettings.gl, name: 'Galician' },
  { code: ChatChannelLanguageSettings.ka, name: 'Georgian' },
  { code: ChatChannelLanguageSettings.el, name: 'Greek' },
  { code: ChatChannelLanguageSettings.gu, name: 'Gujarati' },
  { code: ChatChannelLanguageSettings.ht, name: 'Haitian' },
  { code: ChatChannelLanguageSettings.ha, name: 'Hausa' },
  { code: ChatChannelLanguageSettings.haw, name: 'Hawaiian' },
  { code: ChatChannelLanguageSettings.he, name: 'Hebrew' },
  { code: ChatChannelLanguageSettings.hi, name: 'Hindi' },
  { code: ChatChannelLanguageSettings.hu, name: 'Hungarian' },
  { code: ChatChannelLanguageSettings.is, name: 'Icelandic' },
  { code: ChatChannelLanguageSettings.id, name: 'Indonesian' },
  { code: ChatChannelLanguageSettings.ja, name: 'Japanese' },
  { code: ChatChannelLanguageSettings.jw, name: 'Javanese' },
  { code: ChatChannelLanguageSettings.kn, name: 'Kannada' },
  { code: ChatChannelLanguageSettings.kk, name: 'Kazakh' },
  { code: ChatChannelLanguageSettings.km, name: 'Khmer' },
  { code: ChatChannelLanguageSettings.ko, name: 'Korean' },
  { code: ChatChannelLanguageSettings.lo, name: 'Lao' },
  { code: ChatChannelLanguageSettings.la, name: 'Latin' },
  { code: ChatChannelLanguageSettings.lv, name: 'Latvian' },
  { code: ChatChannelLanguageSettings.ln, name: 'Lingala' },
  { code: ChatChannelLanguageSettings.lt, name: 'Lithuanian' },
  { code: ChatChannelLanguageSettings.lb, name: 'Luxembourgish' },
  { code: ChatChannelLanguageSettings.mk, name: 'Macedonian' },
  { code: ChatChannelLanguageSettings.mg, name: 'Malagasy' },
  { code: ChatChannelLanguageSettings.ms, name: 'Malay' },
  { code: ChatChannelLanguageSettings.ml, name: 'Malayalam' },
  { code: ChatChannelLanguageSettings.mt, name: 'Maltese' },
  { code: ChatChannelLanguageSettings.mi, name: 'Maori' },
  { code: ChatChannelLanguageSettings.mr, name: 'Marathi' },
  { code: ChatChannelLanguageSettings.mn, name: 'Mongolian' },
  { code: ChatChannelLanguageSettings.ne, name: 'Nepali' },
  { code: ChatChannelLanguageSettings.no, name: 'Norwegian' },
  { code: ChatChannelLanguageSettings.nn, name: 'Norwegian Nynorsk' },
  { code: ChatChannelLanguageSettings.oc, name: 'Occitan' },
  { code: ChatChannelLanguageSettings.pa, name: 'Panjabi' },
  { code: ChatChannelLanguageSettings.ps, name: 'Pashto' },
  { code: ChatChannelLanguageSettings.fa, name: 'Persian' },
  { code: ChatChannelLanguageSettings.pl, name: 'Polish' },
  { code: ChatChannelLanguageSettings.ro, name: 'Romanian' },
  { code: ChatChannelLanguageSettings.ru, name: 'Russian' },
  { code: ChatChannelLanguageSettings.sa, name: 'Sanskrit' },
  { code: ChatChannelLanguageSettings.sr, name: 'Serbian' },
  { code: ChatChannelLanguageSettings.sn, name: 'Shona' },
  { code: ChatChannelLanguageSettings.sd, name: 'Sindhi' },
  { code: ChatChannelLanguageSettings.si, name: 'Sinhala' },
  { code: ChatChannelLanguageSettings.sk, name: 'Slovak' },
  { code: ChatChannelLanguageSettings.sl, name: 'Slovenian' },
  { code: ChatChannelLanguageSettings.so, name: 'Somali' },
  { code: ChatChannelLanguageSettings.su, name: 'Sundanese' },
  { code: ChatChannelLanguageSettings.sw, name: 'Swahili' },
  { code: ChatChannelLanguageSettings.sv, name: 'Swedish' },
  { code: ChatChannelLanguageSettings.tl, name: 'Tagalog' },
  { code: ChatChannelLanguageSettings.tg, name: 'Tajik' },
  { code: ChatChannelLanguageSettings.ta, name: 'Tamil' },
  { code: ChatChannelLanguageSettings.tt, name: 'tatar' },
  { code: ChatChannelLanguageSettings.te, name: 'Telugu' },
  { code: ChatChannelLanguageSettings.th, name: 'Thai' },
  { code: ChatChannelLanguageSettings.bo, name: 'Tibetan' },
  { code: ChatChannelLanguageSettings.tr, name: 'Turkish' },
  { code: ChatChannelLanguageSettings.tk, name: 'Turkmen' },
  { code: ChatChannelLanguageSettings.uk, name: 'Ukrainian' },
  { code: ChatChannelLanguageSettings.ur, name: 'Urdu' },
  { code: ChatChannelLanguageSettings.uz, name: 'Uzbek' },
  { code: ChatChannelLanguageSettings.vi, name: 'Vietnamese' },
  { code: ChatChannelLanguageSettings.cy, name: 'Welsh' },
  { code: ChatChannelLanguageSettings.yi, name: 'Yiddish' },
  { code: ChatChannelLanguageSettings.yo, name: 'Yoruba' },
];
